/**
 * "Module beast" avatar presets — typed React port of mockups/avatars.js.
 * One SVG template (viewBox 0 0 96 96): pastel circle background, ink rounded-square
 * head, two finder-eye eyes (the brand motif), one of 7 ear/feature archetypes.
 * 7 archetypes × 7 hues = 49 presets. Gold (hue 85) is excluded — gold stays money.
 */
import type { SVGProps } from 'react';

export const BEAST_ARCHETYPES = [
  'round',
  'tall',
  'pointy',
  'antenna',
  'horns',
  'crest',
  'bare',
] as const;
export type BeastArchetype = (typeof BEAST_ARCHETYPES)[number];

/**
 * The fixed 7-hue avatar wheel (brief §6.1) — hue 85 (gold) intentionally
 * absent. Deliberately NOT tied to the 5 quest-category hues: stored preset
 * ids (`beast/{archetype}/{hue}`) must keep rendering identically.
 */
export const BEAST_HUES = [355, 300, 230, 190, 130, 60, 25] as const;
export type BeastHue = (typeof BEAST_HUES)[number];

export type BeastPresetId = `beast/${BeastArchetype}/${BeastHue}`;

const INK = 'oklch(0.17 0.02 265)';

/** Curated animal-noun → archetype table (brief §6: "animal noun → archetype via a curated table"). */
export const ANIMAL_ARCHETYPES: Readonly<Record<string, BeastArchetype>> = {
  // round ears
  koala: 'round',
  bear: 'round',
  otter: 'round',
  panda: 'round',
  wombat: 'round',
  seal: 'round',
  hamster: 'round',
  mouse: 'round',
  // tall ears
  alpaca: 'tall',
  llama: 'tall',
  rabbit: 'tall',
  hare: 'tall',
  donkey: 'tall',
  kangaroo: 'tall',
  // pointy ears
  fox: 'pointy',
  cat: 'pointy',
  wolf: 'pointy',
  lynx: 'pointy',
  husky: 'pointy',
  coyote: 'pointy',
  // antennae
  robot: 'antenna',
  bug: 'antenna',
  beetle: 'antenna',
  snail: 'antenna',
  moth: 'antenna',
  ant: 'antenna',
  bee: 'antenna',
  // curved horns
  yak: 'horns',
  goat: 'horns',
  ram: 'horns',
  ibex: 'horns',
  buffalo: 'horns',
  ox: 'horns',
  // crest fan (birds)
  parrot: 'crest',
  cockatoo: 'crest',
  peacock: 'crest',
  heron: 'crest',
  hoopoe: 'crest',
  rooster: 'crest',
  // bare + freckles
  blob: 'bare',
  axolotl: 'bare',
  gecko: 'bare',
  frog: 'bare',
  jelly: 'bare',
  newt: 'bare',
  salamander: 'bare',
  // German animals (packages/domain/src/identity/pools-de.ts). Those that are
  // spelt the same in English (koala, otter, wolf, …) are already above.
  'bär': 'round', robbe: 'round', maus: 'round', faultier: 'round',
  alpaka: 'tall', lama: 'tall', hase: 'tall', kaninchen: 'tall', esel: 'tall', 'känguru': 'tall',
  fuchs: 'pointy', katze: 'pointy', luchs: 'pointy', kojote: 'pointy',
  roboter: 'antenna', 'käfer': 'antenna', schnecke: 'antenna', motte: 'antenna', ameise: 'antenna', biene: 'antenna', hummel: 'antenna', libelle: 'antenna',
  ziege: 'horns', widder: 'horns', steinbock: 'horns', 'büffel': 'horns', ochse: 'horns',
  papagei: 'crest', kakadu: 'crest', pfau: 'crest', reiher: 'crest', wiedehopf: 'crest', hahn: 'crest', 'möwe': 'crest', eule: 'crest',
  frosch: 'bare', molch: 'bare', qualle: 'bare', 'kröte': 'bare',
};

/** FNV-1a 32-bit — small, stable, deterministic across runtimes. */
function fnv1a(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

/**
 * Animal noun (last word of "Adjective Animal") → archetype.
 * Curated table first; unknown nouns fall back to a deterministic hash so every
 * name always renders the same beast.
 */
export function nameToArchetype(name: string): BeastArchetype {
  const words = name.trim().toLowerCase().split(/\s+/);
  // The animal is the last word — and in German the last part of a compound:
  // 'Flinke Disco-Möwe' → 'möwe'.
  const noun = (words[words.length - 1] ?? '').split('-').pop() ?? '';
  const curated = ANIMAL_ARCHETYPES[noun];
  if (curated) return curated;
  return BEAST_ARCHETYPES[fnv1a(noun) % BEAST_ARCHETYPES.length] as BeastArchetype;
}

/** hash(fullName) mod 7 → hue (brief §6). */
export function hashToHue(fullName: string): BeastHue {
  return BEAST_HUES[fnv1a(fullName.trim().toLowerCase()) % BEAST_HUES.length] as BeastHue;
}

/** Deterministic preset id for a generated display name: `beast/{archetype}/{hue}`. */
export function presetIdForName(fullName: string): BeastPresetId {
  return `beast/${nameToArchetype(fullName)}/${hashToHue(fullName)}`;
}

/** Parse a stored preset id; returns null on anything malformed. */
export function parsePresetId(id: string): { archetype: BeastArchetype; hue: BeastHue } | null {
  const match = /^beast\/([a-z]+)\/(\d+)$/.exec(id);
  if (!match) return null;
  const archetype = match[1] as BeastArchetype;
  const hue = Number(match[2]) as BeastHue;
  if (!BEAST_ARCHETYPES.includes(archetype)) return null;
  if (!(BEAST_HUES as readonly number[]).includes(hue)) return null;
  return { archetype, hue };
}

/** All 49 preset ids, for pickers/stories. */
export function allPresetIds(): BeastPresetId[] {
  return BEAST_ARCHETYPES.flatMap((a) => BEAST_HUES.map((h): BeastPresetId => `beast/${a}/${h}`));
}

/* Ear/feature layers — geometry identical to mockups/avatars.js. */
function Ears({ type }: { type: BeastArchetype }) {
  switch (type) {
    case 'round':
      return (
        <>
          <circle cx="26" cy="30" r="10" fill={INK} />
          <circle cx="70" cy="30" r="10" fill={INK} />
        </>
      );
    case 'tall':
      return (
        <>
          <rect x="30" y="10" width="10" height="26" rx="5" fill={INK} />
          <rect x="56" y="10" width="10" height="26" rx="5" fill={INK} />
        </>
      );
    case 'pointy':
      return (
        <>
          <polygon points="26,36 30,14 42,32" fill={INK} />
          <polygon points="70,36 66,14 54,32" fill={INK} />
        </>
      );
    case 'antenna':
      return (
        <>
          <rect x="34" y="16" width="4" height="18" rx="2" fill={INK} />
          <circle cx="36" cy="14" r="5" fill={INK} />
          <rect x="58" y="16" width="4" height="18" rx="2" fill={INK} />
          <circle cx="60" cy="14" r="5" fill={INK} />
        </>
      );
    case 'horns':
      return (
        <>
          <path d="M32 34 Q18 30 20 16 Q30 20 38 30 Z" fill={INK} />
          <path d="M64 34 Q78 30 76 16 Q66 20 58 30 Z" fill={INK} />
        </>
      );
    case 'crest':
      return (
        <>
          <rect x="44" y="12" width="8" height="22" rx="4" fill={INK} />
          <rect x="32" y="16" width="8" height="20" rx="4" fill={INK} transform="rotate(-18 36 26)" />
          <rect x="56" y="16" width="8" height="20" rx="4" fill={INK} transform="rotate(18 60 26)" />
        </>
      );
    case 'bare':
      return null;
  }
}

/** White freckles drawn over the head for the `bare` archetype. */
function Freckles() {
  return (
    <>
      <circle cx="32" cy="60" r="1.8" fill="#fff" opacity="0.85" />
      <circle cx="64" cy="60" r="1.8" fill="#fff" opacity="0.85" />
    </>
  );
}

export interface AvatarPresetProps extends Omit<SVGProps<SVGSVGElement>, 'width' | 'height'> {
  /** Stored preset id (`beast/{archetype}/{hue}`); takes precedence over `name`. */
  preset?: BeastPresetId | string;
  /** Display name — derives the preset deterministically when no `preset` given. */
  name?: string;
  size?: number;
  /** Accessible name, e.g. t('ec2.a11y.avatar', {name}). Decorative when omitted. */
  label?: string;
}

/**
 * Renders a module-beast avatar. `preset` (or `name`) → archetype + hue;
 * malformed input falls back to the deterministic hash of whatever was given.
 */
export function AvatarPreset({ preset, name, size = 96, label, ...rest }: AvatarPresetProps) {
  const parsed = (preset && parsePresetId(preset)) || null;
  const archetype = parsed?.archetype ?? nameToArchetype(name ?? preset ?? '');
  const hue = parsed?.hue ?? hashToHue(name ?? preset ?? '');

  return (
    <svg
      viewBox="0 0 96 96"
      width={size}
      height={size}
      role="img"
      aria-label={label}
      aria-hidden={label ? undefined : true}
      data-testid="ec-avatar"
      data-preset={`beast/${archetype}/${hue}`}
      {...rest}
    >
      <circle cx="48" cy="48" r="48" fill={`oklch(0.82 0.11 ${hue})`} />
      <Ears type={archetype} />
      <rect x="24" y="32" width="48" height="42" rx="16" fill={INK} />
      {archetype === 'bare' && <Freckles />}
      <rect x="33" y="45" width="11" height="11" rx="3" fill="#fff" />
      <rect x="36.3" y="48.3" width="4.5" height="4.5" rx="1.4" fill={INK} />
      <rect x="52" y="45" width="11" height="11" rx="3" fill="#fff" />
      <rect x="55.3" y="48.3" width="4.5" height="4.5" rx="1.4" fill={INK} />
      <rect x="43.5" y="64" width="9" height="3.5" rx="1.75" fill="#fff" />
    </svg>
  );
}
